import React from 'react';
import {useLocation, useNavigationType, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {giochiPerPagina, modoPagine} from '../../moduli/leggi';

/**
 * LE PAGINE DEI GIOCHI: una lista lunga (tutti i giochi, una scheda, una
 * ricerca) non si disegna tutta insieme. Ogni pezzo ne mostra quanti dice
 * la skin (modulo «Giochi per pagina», da 50 a 500, di serie 50) e in fondo
 * c'e' «Mostra di piu'» (di serie) o i numeri delle pagine: ‹ 1 2 3 … 9 ›.
 *
 * Copertine e immagini sono la parte pesante: con mille giochi in una
 * griglia sola il browser le chiederebbe tutte. Cosi' ne chiede una pagina.
 *
 * La pagina aperta sta NELL'INDIRIZZO (?pagina=3): aprendo un gioco e
 * tornando indietro si ritrova la stessa pagina, non la prima.
 *
 * `azzera` e' cio' che fa ripartire dalla prima pagina quando cambia (la
 * scheda, la categoria, il testo cercato): la pagina 4 di «Slot» non e'
 * la pagina 4 di «Jackpot». Non azzera all'apertura: tornando indietro la
 * pagina e' quella scritta nell'indirizzo.
 *
 * COL MODO «MOSTRA DI PIU'» (modulo Giochi per pagina › In fondo alla
 * pagina) non si cambia pagina: se ne aggiunge una sotto quelle gia' viste,
 * e ?pagina=3 vuol dire «le prime tre». In fondo, al posto dei numeri, c'e'
 * scritto quanti giochi si sono visti su quanti sono, con una barra, e il
 * tasto; tornando indietro da un gioco si ritrovano tutte le pagine aperte.
 */
export function usePagine(elenco, azzera) {
  const perPagina = giochiPerPagina();
  const altre = modoPagine() === 'altre';
  const [parametri, setParametri] = useSearchParams();
  const pagina = Math.max(1, parseInt(parametri.get('pagina'), 10) || 1);
  const setPagina = (n) => setParametri((prima) => {
    const p = new URLSearchParams(prima);
    if (n > 1) p.set('pagina', String(n));
    else p.delete('pagina');
    return p;
  }, {replace: true});
  const azzeraPrima = React.useRef(azzera);
  React.useEffect(() => {
    if (azzeraPrima.current === azzera) return;
    azzeraPrima.current = azzera;
    if (pagina > 1) setPagina(1);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [azzera]);
  const totale = Math.max(1, Math.ceil(elenco.length / perPagina));
  const qui = Math.min(pagina, totale);
  return {
    visibili: elenco.slice(altre ? 0 : (qui - 1) * perPagina, qui * perPagina),
    pagina: qui,
    totale,
    quanti: elenco.length,
    perPagina,
    altre,
    vai: (n) => {
      setPagina(Math.min(totale, Math.max(1, n)));
      /* Si riparte dall'alto della pagina, come cambiando pagina davvero;
         aggiungendo una pagina sotto, invece, si resta dove si e'. */
      if (!altre && typeof window !== 'undefined' && window.scrollTo) window.scrollTo({top: 0, behavior: 'smooth'});
    },
  };
}

/**
 * TORNARE DOVE SI ERA: chi apre un gioco dalla lobby (o da una categoria, o
 * dalla ricerca) e torna indietro ritrova la pagina allo stesso punto. Il
 * browser da solo non ci riesce: i giochi arrivano dopo, e quando arrivano
 * lui ha gia' rinunciato. Qui si ricorda quanto si era scesi, per ogni voce
 * della cronologia, e si riporta li' quando la lista e' `pronta` e si e'
 * arrivati con «indietro» (o «avanti»). Arrivando da un collegamento si
 * parte dall'alto, come sempre.
 */
export function useRicordaScorrimento(pronta) {
  const {key} = useLocation();
  const comeArrivato = useNavigationType();
  const chiave = 'lvc-scorri:' + key;
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let attesa = 0;
    const salva = () => {
      if (attesa) return;
      attesa = window.requestAnimationFrame(() => {
        attesa = 0;
        try { window.sessionStorage.setItem(chiave, String(Math.round(window.scrollY))); } catch (_) { /* niente memoria: si riparte dall'alto */ }
      });
    };
    window.addEventListener('scroll', salva, {passive: true});
    return () => {
      window.removeEventListener('scroll', salva);
      if (attesa) window.cancelAnimationFrame(attesa);
    };
  }, [chiave]);
  const fatto = React.useRef('');
  React.useEffect(() => {
    if (!pronta || comeArrivato !== 'POP' || fatto.current === chiave || typeof window === 'undefined') return;
    fatto.current = chiave;
    let y = 0;
    try { y = Number(window.sessionStorage.getItem(chiave)) || 0; } catch (_) { y = 0; }
    if (y > 0) window.requestAnimationFrame(() => window.scrollTo(0, y));
  }, [pronta, comeArrivato, chiave]);
}

/**
 * I numeri delle pagine, da mettere sotto la griglia. Con una pagina sola
 * non c'e' niente. Con tante si vedono la prima, l'ultima e le due vicine a
 * quella aperta, e «…» al posto delle altre.
 */
export default function Pagine({pagina, totale, vai, quanti = 0, perPagina = 50, altre = false}) {
  const {t} = useTranslation();
  if (totale <= 1) return null;
  if (altre) {
    const visti = Math.min(quanti, perPagina * pagina);
    return (
      <nav className={'cas-pagine cas-pagine-altre'} aria-label={t('Pagine')}>
        <span className={'cas-pagine-di'}>{t('Hai visto {{visti}} giochi su {{quanti}}', {visti, quanti})}</span>
        <span className={'cas-pagine-barra'} aria-hidden={'true'}><span style={{width: Math.round((visti / quanti) * 100) + '%'}}/></span>
        {pagina < totale && (
          <button type={'button'} className={'cas-pagine-ancora'} onClick={() => vai(pagina + 1)}>{t('Mostra di più')}</button>
        )}
      </nav>
    );
  }
  const numeri = [];
  for (let n = 1; n <= totale; n += 1) {
    if (n === 1 || n === totale || Math.abs(n - pagina) <= 2) numeri.push(n);
    else if (numeri[numeri.length - 1] !== '…') numeri.push('…');
  }
  return (
    <nav className={'cas-pagine'} aria-label={t('Pagine')}>
      <button type={'button'} className={'cas-pagina-n'} disabled={pagina === 1} onClick={() => vai(pagina - 1)}
        aria-label={t('Pagina precedente')}>‹</button>
      {numeri.map((n, i) => (n === '…'
        ? <span key={'r' + i} className={'cas-pagine-salto'} aria-hidden={'true'}>…</span>
        : (
          <button type={'button'} key={n} className={'cas-pagina-n' + (n === pagina ? ' qui' : '')}
            aria-current={n === pagina ? 'page' : undefined} onClick={() => vai(n)}>{n}</button>
        )))}
      <button type={'button'} className={'cas-pagina-n'} disabled={pagina === totale} onClick={() => vai(pagina + 1)}
        aria-label={t('Pagina successiva')}>›</button>
      <span className={'cas-pagine-di'}>{t('Pagina {{pagina}} di {{totale}}', {pagina, totale})}</span>
    </nav>
  );
}
